import 'chart.js/auto';

import { useTheme } from '@brunommsilva/react-foundations';
import { ScriptableContext } from 'chart.js';
import { Chart } from 'react-chartjs-2';

interface Serie {
  label: string;
  values: number[];
  colors?: string[];
  fill?: boolean;
}

interface AxisMark {
  /** The position in [labels] the mark is at. */
  index: number;
  label: string;
}

interface Props {
  title?: string;
  labels: string[];
  series: Serie[];
  /** When given, the horizontal axis only has these points, each with its label and a line across the chart. */
  axisMarks?: AxisMark[];
}

const LineChart = ({ labels, series, axisMarks }: Props) => {
  const { theme } = useTheme();

  return (
    <Chart
      type="line"
      options={{
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        scales: {
          x: {
            grid: {
              display: !!axisMarks,
              color: theme.palette.divider,
              drawTicks: false,
            },
            afterBuildTicks: (axis) => {
              if (axisMarks) {
                axis.ticks = axisMarks.map(({ index }) => ({ value: index }));
              }
            },
            ticks: {
              color: theme.palette.text.secondary,
              maxTicksLimit: axisMarks ? undefined : 8,
              autoSkipPadding: 8,
              maxRotation: 0,
              minRotation: 0,
              callback: (value) =>
                axisMarks
                  ? axisMarks.find(({ index }) => index === value)?.label
                  : labels[Number(value)],
            },
          },
          y: {
            grid: {
              color: theme.palette.divider,
            },
            ticks: {
              color: theme.palette.text.secondary,
              maxTicksLimit: 6,
            },
          },
        },
        plugins: {
          legend: {
            labels: {
              color: theme.palette.text.primary,
              boxWidth: 8,
              boxHeight: 8,
              usePointStyle: true,
              pointStyle: 'circle',
            },
          },
        },
      }}
      data={{
        labels,
        datasets: series.map(({ label, values, colors, fill }) => {
          const baseColor =
            colors && colors[0] ? colors[0] : theme.palette.primary.main;
          return {
            label,
            data: values,
            borderColor: baseColor,
            borderWidth: 2,
            backgroundColor: (context: ScriptableContext<'line'>) => {
              if (!fill) return 'transparent';
              const chart = context.chart;
              const { ctx, chartArea } = chart;
              if (!chartArea) {
                return 'transparent';
              }
              const gradient = ctx.createLinearGradient(
                0,
                chartArea.top,
                0,
                chartArea.bottom
              );
              gradient.addColorStop(0, `${baseColor}26`); // ~15% opacity at the top
              gradient.addColorStop(1, `${baseColor}00`); // 0% opacity at the bottom
              return gradient;
            },
            tension: 0.35,
            fill: fill,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHitRadius: 15,
            pointBackgroundColor: baseColor,
            pointBorderColor: theme.palette.background.paper,
            pointBorderWidth: 2,
          };
        }),
      }}
    />
  );
};

export default LineChart;
